import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Calendar, MessageSquare, Users, Briefcase, Building, Handshake, Rocket } from "lucide-react";

const QuillHistory = () => {
  const timelineEvents = [
    {
      year: "2014",
      title: "Quill Founded",
      description: "Quill was founded as a workplace messaging platform designed to provide a clean, focused communication experience for professional teams.",
      icon: <Rocket className="h-6 w-6" />
    },
    {
      year: "2015",
      title: "Product Development",
      description: "Quill developed its core messaging features, focusing on simplicity and professional use cases. The app gained traction among small to medium-sized teams.",
      icon: <Briefcase className="h-6 w-6" />
    },
    {
      year: "Early 2016",
      title: "Growing Competition",
      description: "The workplace messaging market became increasingly competitive with Slack, HipChat, and Microsoft Teams all vying for market share.",
      icon: <Users className="h-6 w-6" />
    },
    {
      year: "2016",
      title: "Acquired by Slack",
      description: "Slack acquired Quill to bring the talented team and technology into their growing enterprise communication platform. This was part of Slack's strategy to strengthen their product.",
      icon: <Handshake className="h-6 w-6" />
    },
    {
      year: "2016",
      title: "Team Joins Slack",
      description: "Following the acquisition, the Quill team joined Slack to contribute to product development and engineering efforts on the main Slack platform.",
      icon: <Building className="h-6 w-6" />
    },
    {
      year: "2016",
      title: "Quill Discontinued",
      description: "The standalone Quill app was discontinued as the team focused their efforts on improving Slack. Users were encouraged to migrate to Slack.",
      icon: <Calendar className="h-6 w-6" />
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "The History of Quill - From Startup to Slack Acquisition",
    "description": "Explore the history of Quill messaging app from its founding to its acquisition by Slack in 2016.",
    "url": "https://cramstudywith.me/quill/history/",
    "datePublished": "2024-01-15",
    "dateModified": "2024-01-15",
    "author": {
      "@type": "Organization",
      "name": "CramStudyWith.me"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CramStudyWith.me",
      "url": "https://cramstudywith.me"
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>The History of Quill - From Startup to Slack Acquisition | CramStudyWith.me</title>
        <meta name="description" content="Explore the history of Quill messaging app from its founding to its acquisition by Slack in 2016." />
        <meta name="keywords" content="Quill history, Quill Slack acquisition, Quill messaging app, workplace communication history" />
        <link rel="canonical" href="https://cramstudywith.me/quill/history/" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      </Helmet>
      
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              The History of Quill
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              From promising startup to Slack acquisition - explore the story of Quill's journey in the competitive workplace messaging market.
            </p>
            <Link to="/quill/">
              <Button size="lg" variant="outline" className="gap-2">
                <ArrowRight className="h-5 w-5" />
                Find a Quill Alternative
              </Button>
            </Link>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-12 text-center">Quill Timeline</h2>
              <div className="space-y-8">
                {timelineEvents.map((event, index) => (
                  <Card key={index} className="relative overflow-hidden">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary to-secondary" />
                    <CardHeader className="flex flex-row items-center gap-4">
                      <div className="p-3 rounded-full bg-primary/10 text-primary">
                        {event.icon}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-primary">{event.year}</div>
                        <CardTitle className="text-xl">{event.title}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{event.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Acquisition Details Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 text-center">The Slack Acquisition</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Handshake className="h-5 w-5 text-primary" />
                      Acqui-Hire Model
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      The Quill acquisition was primarily an "acqui-hire" - Slack valued the talented team and their expertise in building messaging applications.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="h-5 w-5 text-primary" />
                      Integration into Slack
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      The Quill team's expertise was integrated into Slack's product development, helping to improve the platform's features and user experience.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-primary" />
                      User Migration
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Quill users were encouraged to migrate to Slack, which offered similar functionality with a larger user base and more integrations.
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Rocket className="h-5 w-5 text-primary" />
                      Market Consolidation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      The acquisition was part of a broader trend of consolidation in the workplace messaging market as major players absorbed smaller competitors.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Legacy Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">Quill's Legacy</h2>
              <p className="text-lg text-muted-foreground mb-6">
                While Quill as a standalone product no longer exists, its influence lives on in Slack's continued development. The team's contributions helped shape one of the most successful workplace communication platforms.
              </p>
              <p className="text-lg text-muted-foreground mb-8">
                For users seeking team collaboration tools for studying and learning today, <Link to="/quill/" className="text-primary hover:underline">CramStudyWith.me offers purpose-built alternatives</Link> with video collaboration, productivity tools, and features designed specifically for educational success.
              </p>
              <Link to="/quill/">
                <Button size="lg" className="gap-2">
                  Explore Quill Alternatives
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default QuillHistory;